'use client';

import { VStack, HStack, Box, token } from '@terra/ui-styled-system';
import {
  BodyDefault,
  Card,
  HeadingItem,
  Tooltip,
  Skeleton,
} from '@terra/ui-design-system';
import { HelpCircleIcon } from '@terra/ui-icons';
import type { MouseEventHandler, ReactNode } from 'react';
import { NoData } from './NoData';

type DashboardCardProps = {
  title: string;
  subtitle?: string;
  tooltip?: string;
  icon?: ReactNode;
  trailingContent?: ReactNode;
  children?: ReactNode;
  isLoading?: boolean;
  isEmpty?: boolean;
  emptyLabel?: string;
  contentHeight?: number;
  onClick?: MouseEventHandler<HTMLElement>;
  onContextMenu?: MouseEventHandler<HTMLElement>;
} & Card.RootProps;

export function DashboardCard({
  title,
  subtitle,
  tooltip,
  icon,
  trailingContent,
  children,
  isLoading,
  isEmpty,
  emptyLabel,
  contentHeight,
  onClick,
  onContextMenu,
  ...restProps
}: DashboardCardProps) {
  const hasBody = Boolean(isLoading || isEmpty || children);

  return (
    <Card.Root
      display="flex"
      flexDirection="column"
      boxShadow="none"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border.neutral.muted"
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
      onContextMenu={onContextMenu}
      _hover={onClick ? { borderColor: 'border.neutral.default' } : {}}
      transition="border-color 0.2s"
      {...restProps}
    >
      <Card.Body p={4} display="flex" flexDirection="column" flex={1} minH={0}>
        <VStack gap={6} alignItems="flex-start" w="full" flex={1} minH={0}>
          <VStack gap={1} alignItems="flex-start" w="full">
            <HStack
              gap={2}
              alignItems="center"
              justifyContent="space-between"
              w="full"
            >
              <HStack gap={2} alignItems="center" flexShrink={0}>
                {icon && <Box>{icon}</Box>}
                <HeadingItem color="fg.neutral.default">{title}</HeadingItem>
                {tooltip && (
                  <Tooltip title={tooltip}>
                    <HelpCircleIcon
                      color={token('colors.fg.neutral.muted')}
                      size={16}
                    />
                  </Tooltip>
                )}
              </HStack>
              {trailingContent}
            </HStack>
            {subtitle && (
              <BodyDefault color="fg.neutral.muted">{subtitle}</BodyDefault>
            )}
          </VStack>
          {hasBody ? (
            <Box
              w="full"
              flex={1}
              display="flex"
              flexDirection="column"
              style={{ minHeight: contentHeight }}
            >
              {isLoading ? (
                <Skeleton
                  style={{ height: contentHeight }}
                  width="100%"
                  borderRadius="md"
                />
              ) : isEmpty ? (
                <NoData title={emptyLabel} style={{ height: contentHeight }} />
              ) : (
                children
              )}
            </Box>
          ) : null}
        </VStack>
      </Card.Body>
    </Card.Root>
  );
}
